<script setup lang="ts">
type BasicInfoValue = string | number | null | undefined
type BasicInfoValueInput = BasicInfoValue | BasicInfoValue[]
type ModalFieldKey =
  | 'exchangeAbbreviation'
  | 'regulationStatus'
  | 'companyName'
  | 'exchangeType'
  | 'registeredArea'
  | 'listingInfo'
  | 'stockCode'
  | 'cryptoLoan'
  | 'operatingYears'
  | 'phone'
  | 'customerServiceEmail'
  | 'companyAddress'
  | 'website'
  | 'employees'

type ContactType =
  | 'x'
  | 'telegram'
  | 'facebook'
  | 'instagram'
  | 'medium'
  | 'linkedin'
  | 'youtube'
  | 'reddit'
  | 'discord'
  | 'whatsapp'
  | 'line'
  | 'qq'
  | 'wechat'
  | 'blog'
  | 'tumblr'
  | 'skype'
  | 'steemit'
  | 'weibo'
  | 'other'

interface InfoItem {
  key?: ModalFieldKey | string
  label: string
  value?: BasicInfoValueInput
  values?: BasicInfoValueInput
  isLink?: boolean
  linkUrl?: string
  linkUrls?: string[]
  img?: string | any
  color?: string
}

interface ContactItem {
  key?: ContactType | string
  label: string
  value?: BasicInfoValueInput
  values?: BasicInfoValueInput
  linkUrl?: string
  linkUrls?: string[]
}

interface FieldMeta {
  key: ModalFieldKey
  labelKey: string
  multiline?: boolean
  useLinkValues?: boolean
}

interface NormalizedField {
  key: ModalFieldKey
  label: string
  values: string[]
  urls: string[]
  isEmpty: boolean
  img?: string | any
  color?: string
  renderKey?: string
}

interface NormalizedContactItem {
  type: string
  label: string
  values: string[]
  urls: string[]
  isEmpty: boolean
}

interface HeaderSocialItem {
  type: string
  label: string
  icon: string
}

interface SummaryItem {
  key: string
  label: string
  value: string
  img?: string | any
  isStatus?: boolean
  color?: string
}

type SummarySlot =
  | {
      kind: 'field'
      key: ModalFieldKey
      valueLimit?: number
    }
  | {
      kind: 'contact'
      key: ContactType
      valueLimit?: number
    }

const props = withDefaults(
  defineProps<{
    infoItems?: InfoItem[]
    contactItems?: ContactItem[]
    whatsappUrl?: string
    xUrl?: string
    facebookUrl?: string
    instagramUrl?: string
    exchangeName?: string
    exchangeId?: string
  }>(),
  {
    infoItems: () => [],
    contactItems: () => [],
    whatsappUrl: '',
    xUrl: '',
    facebookUrl: '',
    instagramUrl: '',
    exchangeName: '',
    exchangeId: '',
  },
)

const { locale, t } = useAppI18n()
const { isRtl } = useRtl()
const matomo = useMatomo()
const isMoreDialogOpen = ref(false)
const chineseLocales = new Set(['zh-cn', 'zh-hk', 'zh-tw', 'zh'])
const isChineseLocale = computed(() => chineseLocales.has(locale))

const emptyText = '--'
const dialogTitle = computed(() => t('84023'))
/** 卡片区域标题，仅显示"基础信息"，不加"交易所"前缀 */
const cardTitle = computed(() => t('84023'))
const moreButtonLabel = computed(() => t('82485'))

const fieldMetaMap: Record<ModalFieldKey, FieldMeta> = {
  exchangeAbbreviation: {
    key: 'exchangeAbbreviation',
    labelKey: '82571',
  },
  regulationStatus: {
    key: 'regulationStatus',
    labelKey: '82382',
  },
  companyName: {
    key: 'companyName',
    labelKey: '82570',
  },
  exchangeType: {
    key: 'exchangeType',
    labelKey: '82581',
  },
  registeredArea: {
    key: 'registeredArea',
    labelKey: '82569',
  },
  listingInfo: {
    key: 'listingInfo',
    labelKey: '82573',
  },
  stockCode: {
    key: 'stockCode',
    labelKey: '82574',
  },
  cryptoLoan: {
    key: 'cryptoLoan',
    labelKey: '82582',
  },
  operatingYears: {
    key: 'operatingYears',
    labelKey: '80342',
  },
  phone: {
    key: 'phone',
    labelKey: '82576',
    multiline: true,
  },
  customerServiceEmail: {
    key: 'customerServiceEmail',
    labelKey: '82575',
    multiline: true,
  },
  companyAddress: {
    key: 'companyAddress',
    labelKey: '82578',
    multiline: true,
  },
  website: {
    key: 'website',
    labelKey: '82577',
    multiline: true,
    useLinkValues: true,
  },
  employees: {
    key: 'employees',
    labelKey: '82572',
  },
}

const getFieldLabel = (meta: FieldMeta) => t(meta.labelKey)

const hasOperatingYears = computed(() => props.infoItems.some((item) => item.key === 'operatingYears'))
const modalFieldOrder = computed<ModalFieldKey[][]>(() => {
  const rows: ModalFieldKey[][] = [
    ['exchangeAbbreviation', 'regulationStatus'],
    ['companyName', 'exchangeType'],
    ['registeredArea', 'cryptoLoan'],
  ]

  if (hasOperatingYears.value) {
    rows.push(['operatingYears', 'phone'])
    rows.push(['customerServiceEmail'])
  } else {
    rows.push(['phone', 'customerServiceEmail'])
  }

  return rows
})

const bottomSectionOrder: ModalFieldKey[] = ['companyAddress', 'website']
const contactTypeOrder: ContactType[] = [
  'x',
  'telegram',
  'facebook',
  'instagram',
  'medium',
  'linkedin',
  'youtube',
  'reddit',
  'discord',
  'whatsapp',
  'line',
  'qq',
  'wechat',
  'blog',
  'tumblr',
  'skype',
  'steemit',
  'weibo',
  'other',
]

const contactAliases: Record<string, ContactType> = {
  x: 'x',
  twitter: 'x',
  twitterx: 'x',
  telegram: 'telegram',
  facebook: 'facebook',
  instagram: 'instagram',
  medium: 'medium',
  linkedin: 'linkedin',
  youtube: 'youtube',
  reddit: 'reddit',
  discord: 'discord',
  whatsapp: 'whatsapp',
  line: 'line',
  qq: 'qq',
  wechat: 'wechat',
  weixin: 'wechat',
  blog: 'blog',
  tumblr: 'tumblr',
  skype: 'skype',
  steemit: 'steemit',
  微博: 'weibo',
  weibo: 'weibo',
}

const socialIconBasePath = '/img/exchange-detail/social/'

const normalizeLookupKey = (value: string) =>
  value
    .toLowerCase()
    .replace(/[()\s/_-]+/g, '')
    .replace(/[：:]/g, '')
    .trim()

const sourceInfoItems = computed(() => props.infoItems)
const sourceContactItems = computed(() => props.contactItems)

const isUrl = (value: string) => /^https?:\/\//i.test(value)
const nonLinkContactTypes = new Set(['phone', 'telephone', 'email', 'qq', 'wechat'])
/** 仅使用接口返回的完整链接，电话、邮箱、QQ 和 WeChat 始终保持不可点击。 */
const normalizeContactUrl = (type: string, value: string) => {
  const normalizedValue = value.trim()

  return !nonLinkContactTypes.has(type) && isUrl(normalizedValue) ? normalizedValue : ''
}
const normalizeSingleValue = (value: BasicInfoValue) =>
  value === null || value === undefined ? '' : String(value).trim()
const normalizeValueList = (value: BasicInfoValueInput, splitLines = false) => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeSingleValue(item)).filter(Boolean)
  }

  const normalizedValue = normalizeSingleValue(value)

  if (!normalizedValue) {
    return []
  }

  if (splitLines && normalizedValue.includes('\n')) {
    return normalizedValue
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean)
  }

  return [normalizedValue]
}

const getFieldKeyFromItem = (item: InfoItem) => {
  if (item.key && fieldMetaMap[item.key as ModalFieldKey]) {
    return item.key as ModalFieldKey
  }

  return null
}

const getContactType = (item: ContactItem | InfoItem) => {
  const candidates = [item.key ? String(item.key) : '', item.label]

  for (const candidate of candidates) {
    const normalizedCandidate = normalizeLookupKey(candidate)

    if (contactAliases[normalizedCandidate]) {
      return contactAliases[normalizedCandidate]
    }
  }

  const fallbackType = candidates.map(normalizeLookupKey).find(Boolean)

  return fallbackType || 'other'
}

const getContactLabel = (type: string, fallbackLabel: string) => {
  if (fallbackLabel.trim()) {
    return fallbackLabel.trim()
  }

  if (type === 'wechat') {
    return 'WeChat'
  }

  if (type === 'weibo') {
    return '微博'
  }

  if (type === 'linkedin') {
    return 'LinkedIn'
  }

  return type === 'x' ? 'X' : type.charAt(0).toUpperCase() + type.slice(1)
}

const normalizedFields = computed<Record<ModalFieldKey, NormalizedField>>(() => {
  const fieldMap = Object.values(fieldMetaMap).reduce<Record<ModalFieldKey, NormalizedField>>(
    (result, meta) => {
      result[meta.key] = {
        key: meta.key,
        label: getFieldLabel(meta),
        values: [],
        urls: [],
        isEmpty: true,
      }

      return result
    },
    {} as Record<ModalFieldKey, NormalizedField>,
  )

  sourceInfoItems.value.forEach((item) => {
    const key = getFieldKeyFromItem(item)

    if (!key) {
      return
    }

    const meta = fieldMetaMap[key]
    const rawValues = item.values ?? item.value
    const values = normalizeValueList(rawValues, Boolean(meta.multiline))
    const fallbackUrls = item.linkUrls
      ? item.linkUrls.filter(isUrl)
      : item.linkUrl && isUrl(item.linkUrl)
        ? [item.linkUrl]
        : []
    const urls =
      fallbackUrls.length > 0 ? fallbackUrls : meta.useLinkValues ? values.filter((value) => isUrl(value)) : []

    fieldMap[key] = {
      key,
      label: getFieldLabel(meta),
      values,
      urls,
      isEmpty: values.length === 0,
      img: item.img,
      color: item.color,
      renderKey: `${key}:${values.join('|')}`,
    }
  })

  return fieldMap
})

const normalizedContactItems = computed<NormalizedContactItem[]>(() => {
  const contactMap = new Map<string, NormalizedContactItem>()
  const mergeContactItem = (item: ContactItem | InfoItem) => {
    const type = getContactType(item)
    const values = normalizeValueList(item.values ?? item.value, true)
    const fallbackUrls = item.linkUrls ? item.linkUrls.filter(Boolean) : item.linkUrl ? [item.linkUrl] : []
    const urls = values.map((value, index) => normalizeContactUrl(type, fallbackUrls[index] || value))
    const existingItem = contactMap.get(type)

    if (existingItem) {
      existingItem.values.push(...values)
      existingItem.urls.push(...urls)
      existingItem.isEmpty = existingItem.values.every((value) => value === emptyText)
      return
    }

    contactMap.set(type, {
      type,
      label: getContactLabel(type, item.label),
      values,
      urls,
      isEmpty: values.every((value) => value === emptyText),
    })
  }
  const rawContacts = sourceContactItems.value

  rawContacts.forEach((item) => {
    mergeContactItem(item)
  })

  sourceInfoItems.value.forEach((item) => {
    if (getFieldKeyFromItem(item)) {
      return
    }

    mergeContactItem(item)
  })

  if (props.whatsappUrl && !contactMap.has('whatsapp')) {
    contactMap.set('whatsapp', {
      type: 'whatsapp',
      label: 'WhatsApp',
      values: [props.whatsappUrl],
      urls: [props.whatsappUrl],
      isEmpty: false,
    })
  }

  if (props.xUrl && !contactMap.has('x')) {
    contactMap.set('x', {
      type: 'x',
      label: 'X',
      values: [props.xUrl],
      urls: [props.xUrl],
      isEmpty: false,
    })
  }

  if (props.facebookUrl && !contactMap.has('facebook')) {
    contactMap.set('facebook', {
      type: 'facebook',
      label: 'Facebook',
      values: [props.facebookUrl],
      urls: [props.facebookUrl],
      isEmpty: false,
    })
  }

  if (props.instagramUrl && !contactMap.has('instagram')) {
    contactMap.set('instagram', {
      type: 'instagram',
      label: 'Instagram',
      values: [props.instagramUrl],
      urls: [props.instagramUrl],
      isEmpty: false,
    })
  }

  return Array.from(contactMap.values()).toSorted((first, second) => {
    const firstIndex = contactTypeOrder.indexOf(first.type)
    const secondIndex = contactTypeOrder.indexOf(second.type)
    const normalizedFirstIndex = firstIndex === -1 ? Number.MAX_SAFE_INTEGER : firstIndex
    const normalizedSecondIndex = secondIndex === -1 ? Number.MAX_SAFE_INTEGER : secondIndex

    return normalizedFirstIndex - normalizedSecondIndex
  })
})

const topFieldRows = computed(() =>
  modalFieldOrder.value.map((row) =>
    row.map((key) => {
      const field = normalizedFields.value[key]

      return field.isEmpty
        ? {
            ...field,
            values: [emptyText],
            renderKey: `${field.key}:${emptyText}`,
          }
        : { ...field }
    }),
  ),
)

const bottomSections = computed(() =>
  bottomSectionOrder.map((key) => {
    const field = normalizedFields.value[key]

    return field.isEmpty
      ? {
          ...field,
          values: [emptyText],
          renderKey: `${field.key}:${emptyText}`,
        }
      : { ...field }
  }),
)

const contactSections = computed(() => {
  const nonEmptyContacts = normalizedContactItems.value.filter((item) => item.values.length > 0)

  return nonEmptyContacts.map((item) =>
    item.values.length > 0
      ? item
      : {
          ...item,
          values: [emptyText],
        },
  )
})

const getSummaryValue = (field: NormalizedField, valueLimit?: number) => {
  if (field.isEmpty) {
    return emptyText
  }

  const values = valueLimit ? field.values.slice(0, valueLimit) : field.values

  return values.join(isChineseLocale.value ? '、' : ', ')
}
const toSummaryField = (field: NormalizedField, valueLimit?: number): SummaryItem => ({
  key: field.key,
  label: field.label,
  value: getSummaryValue(field, valueLimit),
  img: field.key === 'registeredArea' ? field.img : undefined,
  isStatus: field.key === 'regulationStatus',
  color: field.color,
})
const getSummaryContactItem = (type: ContactType, valueLimit?: number): SummaryItem => {
  const contact = normalizedContactItems.value.find((item) => item.type === type)
  const fallbackLabel = type === 'x' ? 'Twitter' : getContactLabel(type, '')
  const values = valueLimit ? contact?.values.slice(0, valueLimit) : contact?.values

  return {
    key: `contact-${type}`,
    label: contact?.label || fallbackLabel,
    value: values && values.length > 0 ? values.join(isChineseLocale.value ? '、' : ', ') : emptyText,
  }
}
const leftSummarySlots: SummarySlot[] = [
  { kind: 'field', key: 'registeredArea' },
  { kind: 'field', key: 'operatingYears' },
  { kind: 'field', key: 'companyName' },
  { kind: 'field', key: 'exchangeAbbreviation' },
  { kind: 'field', key: 'employees' },
]
const rightSummarySlots: SummarySlot[] = [
  { kind: 'field', key: 'listingInfo' },
  { kind: 'field', key: 'stockCode' },
  { kind: 'field', key: 'customerServiceEmail', valueLimit: 1 },
  { kind: 'field', key: 'phone', valueLimit: 1 },
  { kind: 'field', key: 'website', valueLimit: 1 },
  ...contactTypeOrder.map((key) => ({ kind: 'contact' as const, key, valueLimit: 1 })),
  { kind: 'field', key: 'companyAddress' },
  { kind: 'field', key: 'exchangeType' },
  { kind: 'field', key: 'cryptoLoan' },
]
const internationalSummarySlots = computed<SummarySlot[]>(() => {
  const slots: SummarySlot[] = [{ kind: 'field', key: 'registeredArea' }]

  if (hasOperatingYears.value) {
    slots.push({ kind: 'field', key: 'operatingYears' })
  }

  slots.push(
    { kind: 'field', key: 'companyName' },
    { kind: 'field', key: 'phone' },
    { kind: 'field', key: 'website' },
    { kind: 'field', key: 'exchangeAbbreviation' },
  )

  return slots
})
const getSummaryItem = (slot: SummarySlot) =>
  slot.kind === 'field'
    ? toSummaryField(normalizedFields.value[slot.key], slot.valueLimit)
    : getSummaryContactItem(slot.key, slot.valueLimit)
const getVisibleSummaryItems = (slots: SummarySlot[], visibleCount: number) => {
  const items = slots.map(getSummaryItem)
  const visibleItems = items.filter((item) => item.value !== emptyText)
  const emptyItems = items.filter((item) => item.value === emptyText)

  return [...visibleItems, ...emptyItems].slice(0, visibleCount)
}
const summaryRows = computed(() => {
  if (!isChineseLocale.value) {
    return internationalSummarySlots.value.map((slot) => [getSummaryItem(slot)])
  }

  const leftItems = getVisibleSummaryItems(leftSummarySlots, 4)
  const rightItems = getVisibleSummaryItems(rightSummarySlots, 4)
  const rowCount = Math.max(leftItems.length, rightItems.length)

  return Array.from({ length: rowCount }, (_, index) => [leftItems[index] || null, rightItems[index] || null])
})

const supportedHeaderIcons = new Set<string>(['whatsapp', 'x', 'facebook', 'instagram', 'telegram', 'linkedin'])
const MAX_HEADER_SOCIAL_ICONS = 3
const headerSocialIconMap: Record<string, string> = {
  whatsapp: `${socialIconBasePath}whatsapp.png`,
  x: `${socialIconBasePath}x.png`,
  facebook: `${socialIconBasePath}facebook.png`,
  instagram: `${socialIconBasePath}instagram.png`,
  telegram: `${socialIconBasePath}telegram.png`,
  linkedin: `${socialIconBasePath}linkedin.png`,
}
const headerSocialItems = computed<HeaderSocialItem[]>(() =>
  contactSections.value
    .filter((item) => supportedHeaderIcons.has(item.type) && !item.isEmpty)
    .map((item) => ({
      type: item.type,
      label: item.label,
      icon: headerSocialIconMap[item.type] || '',
    }))
    .filter((item) => Boolean(item.icon))
    .slice(0, MAX_HEADER_SOCIAL_ICONS),
)
const shouldShowMoreButton = computed(
  () =>
    summaryRows.value.length > 0 ||
    bottomSections.value.some((section) => !section.isEmpty) ||
    contactSections.value.length > 0,
)

const getLinkForIndex = (item: { urls: string[] }, index: number, value: string) => {
  const url = item.urls[index] || value

  return isUrl(url) ? url : ''
}
const { lock: lockPageScroll, unlock: unlockPageScroll } = usePageScrollLock()
const openMoreDialog = () => {
  matomo.trackEvent('broker_detail_left', {
    type_click: '基础信息',
    name: props.exchangeName || '',
    code: props.exchangeId || '',
  })
  if (shouldShowMoreButton.value) {
    isMoreDialogOpen.value = true
  }
}
const closeMoreDialog = () => {
  matomo.trackEvent('broker_base_pop', {
    af_type: '关闭',
    name: props.exchangeName || '',
    code: props.exchangeId || '',
  })
  isMoreDialogOpen.value = false
}
const trackWebsiteClick = (url: string) => {
  matomo.trackEvent('broker_base_pop', {
    af_type: url || '',
    name: props.exchangeName || '',
    code: props.exchangeId || '',
  })
}
const handleWindowKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    closeMoreDialog()
  }
}

watch(isMoreDialogOpen, (isOpen) => {
  if (!import.meta.client) {
    return
  }

  if (isOpen) {
    window.addEventListener('keydown', handleWindowKeydown)
    lockPageScroll()
    return
  }

  window.removeEventListener('keydown', handleWindowKeydown)
  unlockPageScroll()
})

onBeforeUnmount(() => {
  if (!import.meta.client) {
    return
  }

  window.removeEventListener('keydown', handleWindowKeydown)
  unlockPageScroll()
})
</script>

<template>
  <section class="detail-card basic-info-card mt-[2px]" :class="{ 'basic-info-card-international': !isChineseLocale }">
    <div class="basic-info-header">
      <h2 class="card-title">{{ cardTitle }}</h2>
      <button
        v-show="shouldShowMoreButton"
        type="button"
        class="basic-info-more-button"
        :aria-label="moreButtonLabel"
        @click="openMoreDialog"
      >
        <span
          v-for="item in headerSocialItems"
          :key="item.type"
          class="social-badge"
          :class="`social-badge-${item.type}`"
          aria-hidden="true"
        >
          <CommonImage :src="item.icon" :alt="item.label" loading="eager" class="social-badge-img" />
        </span>
        <span class="feedback-arrow" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10.9766 10.0001L6.85156 5.87511L8.0299 4.69678L13.3332 10.0001L8.0299 15.3034L6.85156 14.1251L10.9766 10.0001Z" fill="#7D818A"/>
          </svg>
        </span>
      </button>
    </div>

    <div class="basic-info-summary">
      <template v-for="(row, rowIndex) in summaryRows" :key="`summary-row-${rowIndex}`">
        <div
          v-for="(item, columnIndex) in row"
          :key="item?.key || `summary-empty-${rowIndex}-${columnIndex}`"
          class="basic-info-summary-item"
        >
          <template v-if="item">
            <span class="basic-info-summary-label">{{ item.label }}</span>
            <span
              class="basic-info-summary-value"
              :class="{ 'basic-info-summary-value-status': item.isStatus }"
              :style="item.isStatus && item.color ? { color: item.color } : undefined"
              :title="item.value"
            >
              <CommonImage v-if="item.img" :src="item.img" loading="eager" class="basic-info-flag" alt="" />
              <span
                class="basic-info-summary-value-text"
                :class="{ 'basic-info-summary-value-text-ltr': item.key === 'website' }"
                :dir="item.key === 'website' ? 'ltr' : undefined"
              >
                {{ item.value || emptyText }}
              </span>
            </span>
          </template>
        </div>
      </template>
    </div>

    <Teleport to="body">
      <div v-if="isMoreDialogOpen" class="basic-info-dialog-mask">
        <section
          class="basic-info-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="basic-info-dialog-title"
          @click.stop
        >
          <header class="basic-info-dialog-header">
            <h2 id="basic-info-dialog-title" class="basic-info-dialog-title">{{ dialogTitle }}</h2>
            <button type="button" class="basic-info-dialog-close" @click="closeMoreDialog">
              <span class="basic-info-dialog-close-icon" aria-hidden="true"></span>
            </button>
          </header>

          <div class="basic-info-dialog-body">
            <div class="basic-info-dialog-content">
              <div class="basic-info-dialog-grid">
                <div
                  v-for="(row, rowIndex) in topFieldRows"
                  :key="`top-row-${rowIndex}`"
                  class="basic-info-dialog-grid-row"
                >
                  <div
                    v-for="field in row"
                    :key="field.renderKey || field.key"
                    class="basic-info-dialog-grid-item"
                    :class="{
                      'basic-info-dialog-grid-item-tall':
                        field.key === 'exchangeType' || field.key === 'customerServiceEmail',
                    }"
                  >
                    <p class="basic-info-dialog-label">{{ field.label }}</p>
                    <div
                      class="basic-info-dialog-value-list"
                      :class="{ 'basic-info-dialog-value-list-flag': field.key === 'registeredArea' }"
                    >
                      <template v-if="field.key === 'registeredArea'">
                        <span class="basic-info-dialog-flag-value">
                          <CommonImage
                            v-if="field.img"
                            :src="field.img"
                            loading="eager"
                            class="basic-info-dialog-flag"
                            alt=""
                          />
                          <span class="basic-info-dialog-value">{{ field.values[0] || emptyText }}</span>
                        </span>
                      </template>
                      <template v-else>
                        <p
                          v-for="(value, valueIndex) in field.values"
                          :key="`${field.key}-${valueIndex}`"
                          class="basic-info-dialog-value"
                          :class="{
                            'basic-info-dialog-value-status': field.key === 'regulationStatus' && value !== emptyText,
                          }"
                          :style="field.key === 'regulationStatus' && field.color ? { color: field.color } : undefined"
                        >
                          {{ value || emptyText }}
                        </p>
                      </template>
                    </div>
                  </div>
                </div>
              </div>

              <div class="basic-info-dialog-sections">
                <div v-for="section in bottomSections" :key="section.key" class="basic-info-dialog-section">
                  <p class="basic-info-dialog-label">{{ section.label }}</p>
                  <div class="basic-info-dialog-value-list">
                    <template v-if="section.key === 'website'">
                      <template v-for="(value, valueIndex) in section.values" :key="`${section.key}-${valueIndex}`">
                        <a
                          v-if="getLinkForIndex(section, valueIndex, value)"
                          :href="getLinkForIndex(section, valueIndex, value)"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="basic-info-dialog-link"
                          dir="ltr"
                          @click="trackWebsiteClick(getLinkForIndex(section, valueIndex, value))"
                        >
                          {{ value }}
                        </a>
                        <p v-else class="basic-info-dialog-value">{{ value || emptyText }}</p>
                      </template>
                    </template>
                    <template v-else>
                      <p
                        v-for="(value, valueIndex) in section.values"
                        :key="`${section.key}-${valueIndex}`"
                        class="basic-info-dialog-value"
                      >
                        {{ value || emptyText }}
                      </p>
                    </template>
                  </div>
                </div>

                <div v-for="contact in contactSections" :key="contact.type" class="basic-info-dialog-section">
                  <p class="basic-info-dialog-label">{{ contact.label }}</p>
                  <div class="basic-info-dialog-value-list">
                    <template v-if="!contact.isEmpty">
                      <template v-for="(value, valueIndex) in contact.values" :key="`${contact.type}-${valueIndex}`">
                        <a
                          v-if="getLinkForIndex(contact, valueIndex, value)"
                          :href="getLinkForIndex(contact, valueIndex, value)"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="basic-info-dialog-link"
                          dir="ltr"
                        >
                          {{ value }}
                        </a>
                        <p v-else class="basic-info-dialog-value">{{ value }}</p>
                      </template>
                    </template>
                    <template v-else>
                      <p class="basic-info-dialog-value">{{ emptyText }}</p>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Teleport>
  </section>
</template>

<style scoped lang="less">
.detail-card {
  width: 100%;
  height: 212px;
  min-height: 212px;
  border: 0.5px solid rgba(11, 18, 40, 0.14);
  border-radius: 12px;
  background: #fff;
}

.basic-info-card {
  padding: 20px;
}

.basic-info-card-international {
  display: flex;
  height: 216px;
  flex-direction: column;
  gap: 20px;
}

.basic-info-header {
  display: flex;
  min-height: 28px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.card-title {
  margin: 0;
  color: #292a2e;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
}

.basic-info-card-international .basic-info-header {
  min-height: 26px;
  gap: 8px;
}

.basic-info-card-international .card-title {
  color: #3d3d3d;
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
}

.basic-info-more-button {
  display: inline-flex;
  min-width: 120px;
  min-height: 60px;
  align-items: center;
  justify-content: flex-end;
  gap: 0;
  margin: -16px -20px -16px 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 16px 20px 16px 12px;
}

.basic-info-card-international .basic-info-more-button {
  min-width: 0;
  min-height: 24px;
  margin: 0;
  padding: 0;
}

.social-badge {
  display: inline-flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  position: relative;
  box-sizing: border-box;
  overflow: hidden;
  border: 1px solid #fff;
  border-radius: 50%;
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.social-badge + .social-badge {
  margin-inline-start: -4px;
}

.social-badge:nth-child(1) {
  z-index: 1;
}

.social-badge:nth-child(2) {
  z-index: 2;
}

.social-badge:nth-child(3) {
  z-index: 3;
}

.social-badge-img {
  display: block;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
}

.feedback-arrow {
  width: 20px;
  height: 20px;
  color: #7d818a;
  &:hover {
    color: #292a2e;
  }
}

.feedback-arrow-rtl {
  transform: rotate(180deg);
}

.feedback-arrow :deep(path) {
  fill: currentColor;
}

.basic-info-more-button:focus-visible,
.basic-info-dialog-close:focus-visible,
.basic-info-dialog-link:focus-visible {
  outline: 2px solid rgba(36, 104, 222, 0.35);
  outline-offset: 2px;
}

.basic-info-summary {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
  gap: 12px 24px;
  margin-top: 15px;
}

.basic-info-card-international .basic-info-summary {
  grid-auto-flow: column;
  grid-template-columns: minmax(0, 253px) minmax(0, 1fr);
  grid-template-rows: repeat(3, minmax(0, auto));
  gap: 4px 12px;
  margin-top: 0;
}

.basic-info-summary-item {
  display: grid;
  grid-template-columns: 70px minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.basic-info-card-international .basic-info-summary-item {
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
}

.basic-info-summary-label {
  color: #7d818a;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  white-space: nowrap;
}

.basic-info-card-international .basic-info-summary-label {
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  white-space: normal;
}

.basic-info-summary-value {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  color: #292a2e;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
}

.basic-info-card-international .basic-info-summary-value {
  width: 100%;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
}

.basic-info-summary-value-status {
  color: #1f845a;
}

.basic-info-summary-value-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.basic-info-summary-value-text-ltr {
  direction: ltr;
  text-align: start;
  unicode-bidi: isolate;
}

.basic-info-flag {
  width: 18px;
  height: 12px;
  flex: 0 0 18px;
  border-radius: 1px;
  margin-inline-end: 4px;
  object-fit: cover;
}

.basic-info-dialog-mask {
  position: fixed;
  z-index: 1000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(5, 21, 36, 0.36);
  padding: 24px;
}

.basic-info-dialog {
  position: relative;
  z-index: 1;
  width: min(720px, calc(100vw - 48px));
  max-height: min(600px, calc(100vh - 48px));
  overflow: hidden;
  border-radius: 28px;
  background: #fff;
  box-shadow: 0 24px 60px rgba(5, 21, 36, 0.18);
}

.basic-info-dialog-header {
  position: relative;
  display: flex;
  min-height: 80px;
  align-items: flex-start;
  gap: 8px;
  padding: 32px 32px 0;
}

.basic-info-dialog-header::after {
  position: absolute;
  right: 32px;
  bottom: 0;
  left: 32px;
  height: 0.5px;
  background: rgba(5, 21, 36, 0.06);
  content: '';
}

.basic-info-dialog-title {
  flex: 1 0 0;
  min-width: 0;
  margin: 0;
  color: #292a2e;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
}

.basic-info-dialog-close {
  position: relative;
  z-index: 1;
  display: inline-flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #7d818a;
  cursor: pointer;
  transition: color 0.2s ease;
}

.basic-info-dialog-close-icon {
  width: 32px;
  height: 32px;
  background: url('https://resources1.interface003.com/web30/2026/07/18/c4bf741c-fc52-443e-9be5-8a3f9d8c50bb.svg')
    center / contain no-repeat;
}

.basic-info-dialog-body {
  max-height: calc(min(600px, calc(100vh - 48px)) - 80px);
  overflow-y: auto;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
  scrollbar-width: thin;
}

.basic-info-dialog-body::-webkit-scrollbar,
.basic-info-dialog-body::-webkit-scrollbar:hover {
  width: 2.67px;
}

.basic-info-dialog-body::-webkit-scrollbar-track {
  border-radius: 8px;
  background: transparent;
}

.basic-info-dialog-body::-webkit-scrollbar-thumb {
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.2);
}

.basic-info-dialog-content {
  box-sizing: border-box;
  width: 100%;
  padding: 20px 28px 32px 32px;
}

.basic-info-dialog-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.basic-info-dialog-grid-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
}

.basic-info-dialog-grid-item,
.basic-info-dialog-section {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 8px;
  border-bottom: 0.5px solid rgba(5, 21, 36, 0.06);
  padding-bottom: 12px;
}

.basic-info-dialog-label {
  margin: 0;
  color: #7d818a;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
}

.basic-info-dialog-value-list {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 8px;
}

.basic-info-dialog-value-list-flag {
  gap: 0;
}

.basic-info-dialog-flag-value {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.basic-info-dialog-flag {
  width: 18px;
  height: 12px;
  flex: 0 0 18px;
  border-radius: 1px;
  object-fit: cover;
}

.basic-info-dialog-value,
.basic-info-dialog-link {
  margin: 0;
  color: #292a2e;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  word-break: break-word;
}

.basic-info-dialog-link {
  direction: ltr;
  text-align: left;
  text-decoration: none;
  transition: color 0.2s ease;
  unicode-bidi: isolate;
}

.basic-info-dialog-link:hover {
  color: #0d3f63;
}

html[dir='rtl'] .basic-info-dialog-link {
  text-align: right;
}

.basic-info-dialog-sections {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
}

@media screen and (max-width: 1180px) {
  .basic-info-summary {
    gap: 14px 20px;
  }
}

@media screen and (max-width: 800px) {
  .basic-info-dialog-mask {
    padding: 16px;
  }

  .basic-info-dialog {
    width: calc(100vw - 32px);
  }

  .basic-info-dialog-header {
    padding: 24px 20px 0;
  }

  .basic-info-dialog-header::after {
    right: 20px;
    left: 20px;
  }

  .basic-info-dialog-body {
    max-height: calc(100vh - 128px);
  }

  .basic-info-dialog-content {
    padding: 20px 16px 24px 20px;
  }
}

@media screen and (max-width: 680px) {
  .basic-info-summary,
  .basic-info-dialog-grid-row {
    grid-template-columns: 1fr;
  }

  .basic-info-card-international .basic-info-summary {
    grid-auto-flow: row;
    grid-template-columns: 1fr;
    grid-template-rows: none;
  }

  .basic-info-summary-item {
    grid-template-columns: 70px minmax(0, 1fr);
  }
}
</style>
